import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/shared/ui/card';
import { Skeleton } from '@/shared/ui/skeleton';
import { Progress } from '@/shared/ui/progress';
import { Badge } from '@/shared/ui/badge';
import { TrendingUp, Lightbulb } from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell,
  RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar,
  LineChart, Line,
} from 'recharts';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/features/auth/components/AuthContext';
import { format } from 'date-fns';

interface ScoreFactor {
  factor: string;
  value: number;
  maxValue: number;
  percentage: number;
}

interface HistoryPoint {
  date: string;
  score: number;
  change: number;
  reason: string;
}

export const ScoreAnalytics: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [factors, setFactors] = useState<ScoreFactor[]>([]);
  const [history, setHistory] = useState<HistoryPoint[]>([]);
  const [totalScore, setTotalScore] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user?.id) return;
    fetchData();
  }, [user?.id]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [scoreResult, historyResult] = await Promise.all([
        supabase
          .from('credit_scores')
          .select('total_score, photo_consistency_score, activity_logging_score, yield_accuracy_score, network_engagement_score, verification_compliance_score, engagement_quality_score')
          .eq('user_id', user!.id)
          .maybeSingle(),
        supabase
          .from('score_histories')
          .select('total_score, score_change, change_reason, created_at')
          .eq('user_id', user!.id)
          .order('created_at', { ascending: true })
          .limit(60),
      ]);

      if (scoreResult.data) {
        const d = scoreResult.data;
        setTotalScore(d.total_score);
        const maxPerFactor = 100;
        setFactors([
          { factor: t('analytics.score.photoConsistency', 'Photo Consistency'), value: d.photo_consistency_score, maxValue: maxPerFactor, percentage: d.photo_consistency_score },
          { factor: t('analytics.score.activityLogging', 'Activity Logging'), value: d.activity_logging_score, maxValue: maxPerFactor, percentage: d.activity_logging_score },
          { factor: t('analytics.score.yieldAccuracy', 'Yield Accuracy'), value: d.yield_accuracy_score, maxValue: maxPerFactor, percentage: d.yield_accuracy_score },
          { factor: t('analytics.score.network', 'Network'), value: d.network_engagement_score, maxValue: maxPerFactor, percentage: d.network_engagement_score },
          { factor: t('analytics.score.verificationCompliance', 'Verification Compliance'), value: d.verification_compliance_score, maxValue: maxPerFactor, percentage: d.verification_compliance_score },
          { factor: t('analytics.score.engagementQuality', 'Engagement Quality'), value: d.engagement_quality_score, maxValue: maxPerFactor, percentage: d.engagement_quality_score },
        ]);
      }

      setHistory(
        (historyResult.data ?? []).map(h => ({
          date: format(new Date(h.created_at), 'MMM dd'),
          score: h.total_score,
          change: h.score_change,
          reason: h.change_reason,
        }))
      );
    } catch (error) {
      console.error('Failed to fetch score analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-64 w-full" />
        <Skeleton className="h-48 w-full" />
      </div>
    );
  }

  if (factors.length === 0) {
    return (
      <Card>
        <CardContent className="p-8 text-center">
          <TrendingUp className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-foreground mb-2">{t('analytics.score.noData', 'No Score Data Yet')}</h3>
          <p className="text-sm text-muted-foreground">{t('analytics.score.noDataDesc', 'Your credit score will appear here once calculated.')}</p>
        </CardContent>
      </Card>
    );
  }

  const radarData = factors.map(f => ({ factor: f.factor.split(' ')[0], value: f.percentage }));

  return (
    <div className="space-y-6">
      {/* Overall Score */}
      <Card>
        <CardHeader>
          <CardTitle>{t('analytics.score.overall', 'Overall Credit Score')}</CardTitle>
          <CardDescription>{t('analytics.score.overallDesc', 'Your current score breakdown')}</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="text-center mb-6">
            <div className="text-5xl font-bold text-primary mb-2">{totalScore}</div>
            <div className="text-sm text-muted-foreground">{t('analytics.score.totalCreditScore', 'Total credit score')}</div>
          </div>
        </CardContent>
      </Card>

      {/* Score Factors */}
      <Card>
        <CardHeader>
          <CardTitle>{t('analytics.score.scoreFactors', 'Score Factors')}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {factors.map((item, index) => (
              <div key={index} className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="font-medium text-sm text-foreground">{item.factor}</span>
                  <div className="flex items-center gap-2">
                    <Badge variant="secondary" className="text-xs">{item.percentage}%</Badge>
                    <span className="text-sm font-semibold text-foreground">{item.value}/{item.maxValue}</span>
                  </div>
                </div>
                <Progress value={item.percentage} className="h-2" />
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Visual Charts */}
      <Card>
        <CardHeader>
          <CardTitle>{t('analytics.score.distribution', 'Score Distribution')}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={factors}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis dataKey="factor" stroke="hsl(var(--muted-foreground))" fontSize={10} angle={-45} textAnchor="end" height={80} />
                  <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} />
                  <Tooltip contentStyle={{ backgroundColor: 'hsl(var(--popover))', border: '1px solid hsl(var(--border))', borderRadius: '8px' }} />
                  <Bar dataKey="value" radius={4}>
                    {factors.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.percentage >= 90 ? 'hsl(142 76% 36%)' : entry.percentage >= 70 ? 'hsl(var(--primary))' : 'hsl(var(--muted))'} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div>
              <ResponsiveContainer width="100%" height={300}>
                <RadarChart data={radarData}>
                  <PolarGrid stroke="hsl(var(--border))" />
                  <PolarAngleAxis dataKey="factor" stroke="hsl(var(--muted-foreground))" fontSize={12} />
                  <PolarRadiusAxis angle={90} domain={[0, 100]} stroke="hsl(var(--muted-foreground))" fontSize={10} />
                  <Radar name="Score" dataKey="value" stroke="hsl(var(--primary))" fill="hsl(var(--primary))" fillOpacity={0.3} />
                  <Tooltip contentStyle={{ backgroundColor: 'hsl(var(--popover))', border: '1px solid hsl(var(--border))', borderRadius: '8px' }} />
                </RadarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Score History */}
      {history.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>{t('analytics.score.history', 'Score History')}</CardTitle>
            <CardDescription>{t('analytics.score.historyDesc', 'Your score over time')}</CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={250}>
              <LineChart data={history}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="date" stroke="hsl(var(--muted-foreground))" fontSize={12} />
                <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} />
                <Tooltip contentStyle={{ backgroundColor: 'hsl(var(--popover))', border: '1px solid hsl(var(--border))', borderRadius: '8px' }} />
                <Line type="monotone" dataKey="score" stroke="hsl(var(--primary))" strokeWidth={3} dot={{ fill: 'hsl(var(--primary))', r: 3 }} />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      {/* Improvement Suggestions */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Lightbulb className="h-5 w-5" />
            {t('analytics.score.improvements', 'Improvement Suggestions')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {factors
              .filter(item => item.percentage < 85)
              .sort((a, b) => a.percentage - b.percentage)
              .slice(0, 3)
              .map((item, index) => (
                <div key={index} className="p-4 rounded-lg border bg-card">
                  <div className="flex items-start justify-between mb-2">
                    <h4 className="font-semibold text-sm text-foreground">{item.factor}</h4>
                    <Badge variant="secondary">{item.percentage}%</Badge>
                  </div>
                  <p className="text-xs text-foreground">
                    Improve this factor to gain up to {item.maxValue - item.value} additional points
                  </p>
                </div>
              ))}
            {factors.every(f => f.percentage >= 85) && (
              <p className="text-sm text-muted-foreground text-center py-4">{t('analytics.score.allGood', 'All factors are performing well!')}</p>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
